import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { H5 } from "simplydo/react/ui/header";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import { useAppSelector } from "store";
import util from "utils/utils";

type ConfirmAnnouncementModalProps = {
  handleConfirm: (sendAnnouncement: Array<string>) => void;
  modalOpen: boolean;
  announcements: Array<OpenAPI.Schemas["Announcement"]>;
  ideaCount: number;
  onClose: () => void;
};

const ConfirmAnnouncementModal = ({
  handleConfirm,
  modalOpen,
  announcements,
  ideaCount,
  onClose,
}: ConfirmAnnouncementModalProps) => {
  const { t } = useTranslation();
  const params = useParams();
  const user = useAppSelector((state) => state.user);
  const [announcementsToTrigger, setAnnouncementsToTrigger] = useState<
    Array<string>
  >(announcements.map((announcement) => announcement._id));

  useEffect(() => {
    setAnnouncementsToTrigger(
      announcements.map((announcement) => announcement._id),
    );
  }, [announcements]);

  const toUserConditionMap = {
    userThatTriggered: "User that triggered the announcement",
    customUsers: "A custom set of users",
    ideaOwner: `Users that own or collaborate on the ${t("generic.idea")}`,
  };
  return (
    <Modal open={modalOpen} onClose={() => onClose()}>
      <ModalHeader>
        {util.pluralise(
          ideaCount,
          t("common:capitalise", { key: "generic.idea" }),
          t("common:capitalise", { key: "generic.ideas" }),
          false,
        )}{" "}
        was moved
      </ModalHeader>
      <ModalContent>
        <p>
          There{" "}
          {util.pluralise(
            announcements.length,
            "is a project lane announcement",
            "are project lane announcements",
            false,
          )}{" "}
          configured when {t("generic.ideas")} are moved to this lane.{" "}
          {util.hasPermission(user, "challenge.viewSettings", params.id) ||
          util.hasPermission(
            user,
            "org.manageChallenges",
            user?.ownerOrganisation?._id,
          ) ? (
            <>
              You can review announcements in the{" "}
              <Link
                to={`/challenges/${params.id}/settings/announcements`}
                target="_blank"
              >
                {t("generic.challenge")} settings
              </Link>
              .
            </>
          ) : null}
        </p>
        <H5>
          Lane{" "}
          {util.pluralise(
            announcements.length,
            "announcement",
            "announcements",
            false,
          )}
        </H5>
        {announcements.map((announcement) => (
          <Checkbox
            toggle
            key={announcement._id}
            checked={announcementsToTrigger.includes(announcement._id)}
            onChange={(_e, d) => {
              if (d.checked) {
                setAnnouncementsToTrigger([
                  ...announcementsToTrigger,
                  announcement._id,
                ]);
              } else {
                setAnnouncementsToTrigger(
                  announcementsToTrigger.filter((a) => a !== announcement._id),
                );
              }
            }}
          >
            <CheckboxHeader>{announcement.subject}</CheckboxHeader>
            <CheckboxContent>
              <span>
                {/* @ts-ignore */}
                Recipients:{" "}
                <b>
                  {
                    toUserConditionMap[
                      announcement.toUserCondition || "userThatTriggered"
                    ]
                  }
                </b>
              </span>
            </CheckboxContent>
          </Checkbox>
        ))}
        <br />
        <p>
          Do you want to inform the recipients above about the progression of
          the moved{" "}
          {util.pluralise(
            ideaCount,
            t("generic.idea"),
            t("generic.ideas"),
            false,
          )}
          ?
        </p>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => handleConfirm([])}>
          Skip{" "}
          {util.pluralise(
            announcementsToTrigger.length,
            "announcement",
            "announcements",
            false,
          )}
        </Button>
        <Button
          onClick={() => handleConfirm(announcementsToTrigger)}
          disabled={!announcementsToTrigger.length}
          color="primary"
        >
          Send{" "}
          {util.pluralise(
            announcementsToTrigger.length,
            "announcement",
            "announcements",
            false,
          )}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default ConfirmAnnouncementModal;
